import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Avatar } from "@/components/Avatar";
import { FlagsTicker } from "@/components/FlagsTicker";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { brandImage } from "@/lib/brand";
import { ArrowRight } from "lucide-react";
import { API, formatError } from "@/lib/api";
import { formatMoney } from "@/lib/utils";

const mediaSrc = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const isVideo = (u) => /\.(mp4|mov|webm)(\?|$)/i.test(u || "");

export default function PublicPay() {
  const { lid } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const { branding } = useAuth();
  const { theme } = useTheme();
  const bg = brandImage(branding, theme, "pay_bg") || "";

  useEffect(() => { (async () => {
    try { const { data } = await axios.get(`${API}/public/pay/${lid}`); setInfo(data); }
    catch (e) { setErr(formatError(e)); }
  })(); }, [lid]);

  return (
    <div className="stream-dark min-h-screen flex items-center justify-center px-4 pt-10 pb-24 relative overflow-hidden">
      {bg && (isVideo(bg) ? (
        <video src={bg} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover z-0" data-testid="pay-bg-video" />
      ) : (
        <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover z-0" data-testid="pay-bg" />
      ))}
      <div className="w-full max-w-md relative z-10">
        <div className="flex justify-end mb-3"><ThemeToggle /></div>
        <Link to="/" className="pay-logo-wrap flex justify-center mb-6 sm:mb-8 mx-auto w-fit"><Logo size="xl" light pay /></Link>
        <div className="card-dark p-5 sm:p-8 overflow-hidden backdrop-blur-xl bg-[#171717]/85 border-white/10">
          {err ? (
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-heading font-bold text-white mb-2">Link nije dostupan</div>
              <div className="text-white/60 text-sm sm:text-base">{err}</div>
            </div>
          ) : !info ? (
            <div className="text-center text-white/50">Učitavanje…</div>
          ) : (
            <>
              <div className="flex items-center gap-4 sm:gap-5 mb-5 pb-5 sm:mb-6 sm:pb-6 border-b border-white/10">
                <div className="shrink-0 rounded-full p-[3px] bg-gradient-to-br from-[#FFD60A] to-[#FFD60A]/30" data-testid="pay-avatar-ring">
                  <div className="rounded-full overflow-hidden bg-[#171717] p-[2px]">
                    <Avatar user={info.owner} size={84} />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-white/50 text-[10px] sm:text-xs uppercase tracking-widest">Uplata za</div>
                  <div className="text-white font-heading font-bold text-lg sm:text-xl truncate">{info.owner?.first_name} {info.owner?.last_name}</div>
                  <div className="text-white/40 text-xs sm:text-sm truncate">@{info.owner?.username}</div>
                </div>
              </div>
              <div className="text-center py-2 sm:py-4">
                <div className="text-white/50 text-[10px] sm:text-xs uppercase tracking-widest mb-2">Iznos</div>
                <div className="font-heading font-black text-5xl sm:text-6xl text-[#FFD60A] break-words">{formatMoney(info.amount)}</div>
                <div className="text-white/60 text-xl sm:text-2xl mt-1">{info.currency}</div>
              </div>
              <form method="POST" action={`${API}/public/pay/${lid}/go`}>
                <button
                  type="submit"
                  className="btn-yellow w-full h-12 sm:h-14 rounded-full text-sm sm:text-base mt-5 sm:mt-6 inline-flex items-center justify-center gap-2"
                  data-testid="public-pay-btn"
                >
                  PLATI PUTEM STRIPE <ArrowRight size={18} />
                </button>
              </form>
              <p className="text-white/40 text-xs text-center mt-4">Sigurno plaćanje putem Stripe</p>

              {info.description?.trim() && (
                <div className="marquee-mask mt-5 sm:mt-6 -mx-5 -mb-5 sm:-mx-8 sm:-mb-8 overflow-hidden rounded-b-[inherit] bg-[#FFD60A] py-2.5 sm:py-3" data-testid="pay-message-ticker">
                  <div className="marquee-track">
                    {[0, 1, 2, 3].map((i) => (
                      <span key={i} className="text-black font-heading font-bold text-xs sm:text-sm uppercase tracking-widest px-5 sm:px-6" aria-hidden={i > 0}>
                        {info.description} &nbsp;•&nbsp;
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <FlagsTicker />
    </div>
  );
}
